import { ActionField, ActionPlanAction, TargetSsqProperty } from "src/api/client-api/ActionPlan/ActionPlanAction";
import { formatNumber } from "src/utils/formatting";

export const getImpact = (actionPlanAction: ActionPlanAction, scoreId: number): number | null => {
    if (Array.isArray(actionPlanAction.totalImpact)) return null;
    const impact = actionPlanAction.totalImpact[scoreId.toString()];

    return impact;
};

export const getImpactPercentage = (actionPlanAction: ActionPlanAction, scoreId: number): number | null => {
    return actionPlanAction.totalImpactPercentage[scoreId] ?? null;
};

export const getFormattedImpact = (impact: number | null): string | null => {
    if (impact === null) return null;
    if (isNaN(impact)) return null;

    return formatNumber(impact);
};

export const getFormattedFieldValue = (value: string): string => {
    return value.replace(/ (str|int)$/, "");
};

export const getActionFieldsTargetProperties = (actionFields: ActionField[]): TargetSsqProperty[] => {
    const targetProperties = actionFields.flatMap((field) => {
        if (field.actionFormulas === undefined) return [];

        return field.actionFormulas.flatMap((formula) => {
            if (formula.targetSsqProperty === undefined) return [];

            return formula.targetSsqProperty;
        });
    });

    return targetProperties;
};

export const isSomeStartingDataInsufficient = (
    actionFields: ActionField[],
    actionPlanAction: ActionPlanAction | undefined,
): boolean => {
    // Maybe it must be false...
    if (!actionPlanAction) return true;

    const impactData = actionPlanAction.impactData;
    const isImpactDataEmpty = Object.keys(impactData).length === 0;

    if (isImpactDataEmpty) return true;

    const actionFieldProperties = getActionFieldsTargetProperties(actionFields);
    const actionFieldPropertiesIds = actionFieldProperties.map((property) => property.id);
    const impactPropertiesIds = Object.keys(impactData).map((propertyIdString) => Number(propertyIdString));

    const missingImpacts = impactPropertiesIds.filter((propertyId) => !actionFieldPropertiesIds.includes(propertyId));

    if (missingImpacts.length > 0) return true;

    return Object.values(actionPlanAction.impactData).some((impactsDataPerPosition) => {
        if (impactsDataPerPosition === undefined) {
            return false;
        }
        // Handle both array and object formats (objects occur when arrays have gaps)
        const positionValues = Object.values(impactsDataPerPosition);

        return positionValues.some((impactsDataForAllActions) => {
            if (impactsDataForAllActions === undefined) {
                return false;
            }

            const impactDataForAction = impactsDataForAllActions.find(
                (impactData) => impactData.actionId === actionPlanAction.actionId,
            );

            if (impactDataForAction === undefined) return false;

            return impactDataForAction.startElement === null || impactDataForAction.startElement === "";
        });
    });
};

export const isFieldStartingDataInsufficient = (
    field: ActionField,
    actionPlanAction: ActionPlanAction | undefined,
): boolean => {
    const impactData = actionPlanAction?.impactData ?? {};
    const actionFieldProperties = field.actionFormulas?.map((formula) => formula.targetSsqProperty) ?? [];
    const actionFieldPropertiesIds = actionFieldProperties
        .filter((property) => property !== undefined)
        .map((property) => property?.id);

    return actionFieldPropertiesIds.some((propertyId) => {
        return impactData[propertyId] === undefined;
    });
};

export const isUnitlessAbbreviation = (abbreviation: string | undefined): boolean =>
    abbreviation === "str" || abbreviation === "int";

export const getActionFieldUnit = (actionField: ActionField): string | undefined => {
    const hasDisplayTypeWithUnit = actionField.contributionTypes.some(
        (type) => type.toLowerCase().includes("difference") || type.toLowerCase().includes("absolute"),
    );

    const abbreviation = actionField.unit?.abbreviation;

    if (hasDisplayTypeWithUnit && !isUnitlessAbbreviation(abbreviation)) {
        return abbreviation;
    }

    const hasPercentageType = actionField.contributionTypes.some(
        (type) => type.toLowerCase().includes("percentage") || type === "%",
    );

    if (hasPercentageType === true) return "%";

    return undefined;
};
